import {classNames, React, observer, moment, useLocalStore, pluralize, jsondiff, useAsObservableSource} from '@api'
import {Icon, IconName} from '@blueprintjs/core'
import {SkeletonGlowEvent} from './'
import {computed, toJS} from 'mobx'
import {Fragment, useContext} from 'react'
import ReactTimeago from 'react-timeago'
import * as css from './CatalogItemChangeEvent.css'
import {nav, IUser, deployments, ICatalogFieldConfig, ICatalogItemChangeEventRaw, useCatalog, StoresInstance} from '@stores'
import {MyImage, Placeholder} from 'centric-ui-components'
import {useStores, useVipLocalStore, VipLocalStore} from '@design-board'

/**
 * Todo
 *  Image changes (add/delete/change default)
 *  Card Preview popover for assigned/unassigned specs
 *  When assignment and changing a child attribute happen in the same update the GUI only reflects the assignment
 */

export const verbForChangeEvent = ({operation}: ICatalogItemChangeEventRaw) => {
  switch (operation) {
    case 'ICREATE': {
      return 'added an image:'
    }

    case 'IUPDATE': {
      return 'replaced an image:'
    }

    case 'IDELETE': {
      return 'removed an image:'
    }

    case 'CREATE': {
      return 'created'
    }

    case 'UPDATE': {
      return 'modified'
    }

    case 'DELETE': {
      return 'deleted'
    }
  }

  return 'unknown'
}

export const CatalogItemChangeEventStoreContext = React.createContext<CatalogItemChangeEventStore>(undefined)
export const useCatalogItemChangeEventStore = () => useContext(CatalogItemChangeEventStoreContext)

export const EventChangeRendererStoreContext = React.createContext<IndividualEventChangeStore>(undefined)
export const useEventChangeRendererStore = () => useContext(EventChangeRendererStoreContext)

// region Individual changes within an event

class IndividualEventChangeStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: { path: string, inline?: boolean, value }, public store: CatalogItemChangeEventStore) {
    super(stores)
  }

  @computed get field() { return this.store.fieldByName(this.props.path)}

  @computed get isUnhandled() {
    return !(this.isAssign || this.isUnassign || this.isChange)
  }

  @computed get isAssign() {
    const {beforeValue, field} = this
    return field?.esIndex && !beforeValue
  }

  @computed get isUnassign() {
    const {afterValue, field} = this
    return field?.esIndex && !afterValue
  }

  @computed get isChange() {
    const {store: {data: {before, after}}, props: {path}} = this
    return _.get(before, path)?.id !== _.get(after, path)?.id
  }

  @computed get associatedWorkspace() {
    const {field, catalog} = this

    return field?.esIndex && catalog.fieldByWorkspaceIndex[field?.esIndex]
  }

  @computed get beforeValue() {
    const {props: {path}, store: {data: {before}}} = this
    return !_.isEmpty(before) ? _.get(before, path) : null
  }

  @computed get afterValue() {
    const {props: {path}, store: {data: {after}}} = this
    return !_.isEmpty(after) ? _.get(after, path) : null
  }

  get sidebar() { return this.stores.sidebar}

  @computed get beforeFieldUrl() {
    const {associatedWorkspace, beforeValue, catalog, sidebar} = this
    return _.isEmpty(beforeValue) || !associatedWorkspace
           ? null : nav.urlFor_string({workspace: associatedWorkspace.workspace?.config, queryParams: {deployment: deployments.selectedDeploymentId, ...sidebar.queryParamsFor({right: {open: true, path: [{where: [{value: catalog.idFor(beforeValue)}]}]}})}})
  }

  @computed get afterFieldUrl() {
    const {afterValue, associatedWorkspace, catalog, sidebar} = this
    return _.isEmpty(afterValue) || !associatedWorkspace
           ? null : nav.urlFor_string({workspace: associatedWorkspace.workspace?.config, queryParams: {deployment: deployments.selectedDeploymentId, ...sidebar.queryParamsFor({right: {open: true, path: [{where: [{value: catalog.idFor(afterValue)}]}]}})}})
  }

  @computed get attrKeys() {
    const {store: {delta}, props: {path}} = this
    if (path === 'attributes') {
      return Object.keys(_.get(delta, path))
    }
    return []
  }

  @computed get label() {
    const {store: {fieldByName}, props: {path}} = this,
      field = fieldByName(path)
    return field?.label || field?.def?.displayName?.singular
  }
}

const IndividualEventChange = observer<{ inline?: boolean, change: { path, value } }>(({inline, change: {path, value}}) => {
  const obsProps = useAsObservableSource({path, value, inline}),
    eventStore = useCatalogItemChangeEventStore(),
    store = useVipLocalStore((ws) => new IndividualEventChangeStore(ws, obsProps, eventStore)),
    {field, store: {isImageEvent, item: {operation}, loading}, isUnhandled} = store

  return (
    loading || (!isImageEvent && (!field || field.card?.hide)) ? null :
    <EventChangeRendererStoreContext.Provider value={store}>
      {!inline && <Icon icon='dot' className={css.bullet}/>}
      {operation == 'ICREATE' || operation == 'IDELETE' || operation == 'IUPDATE' ? <ImageChangeRenderer/> : field?.esIndex && !isUnhandled ? <SpecChangeRenderer/> : <ScalarChangeRenderer/>}
    </EventChangeRendererStoreContext.Provider>
  )
})

const ImageChangeRenderer = observer<{ className?: string }>(function ImageChangeRenderer({className}) {
  const renderStore = useEventChangeRendererStore(),
    catalog = useCatalog(),
    {props: {value}} = renderStore,
    imgUrl = catalog.imageUrlFor(_.last(value))

  //console.log({imgUrl, field, value})
  return <div className={classNames(css.jsondiff, className)}>
    <MyImage imageUrl={imgUrl}/>
  </div>
})

const SpecChangeRenderer = observer(function SpecChangeRenderer() {
  const renderStore = useEventChangeRendererStore(),
    {strings, catalog, props: {inline}, afterFieldUrl, beforeFieldUrl, beforeValue, afterValue, isAssign, isChange, isUnassign, associatedWorkspace, store: {data}} = renderStore,
    dataMap = catalog.dataMapFor(associatedWorkspace?.workspace?.dataMap),
    beforeSpan = <span className={css.spec}>{`${beforeValue === null ? strings.noValue : _.get(beforeValue, dataMap.title) ?? strings.noValue}`}</span>,
    afterSpan = <span className={css.spec}>{`${afterValue === null ? strings.noValue : _.get(afterValue, dataMap.title) ?? strings.noValue}`}</span>

  let {label} = renderStore
  //console.log({beforeValue, afterValue, isAssign, isChange, isUnassign, label})
  switch (true) {
    case isAssign: {
      return (
        <div className={css.jsondiff} data-action='assign'>
          {!inline ?
           <Fragment>
             <span className={css.text}>Assigned</span>
             {afterFieldUrl ? <a href={afterFieldUrl} target={'_blank'}>{afterSpan}</a> : afterSpan}
             <span className={css.text}>to</span>
             <span className={css.attributeName}>{label}</span>
           </Fragment> :
           <Fragment>
             <span className={css.attributeName}>{label}</span>
             <span className={css.text}>to</span>
             {afterFieldUrl ? <a href={afterFieldUrl} target={'_blank'}>{afterSpan}</a> : afterSpan}
           </Fragment>}
        </div>
      )
    }
    case isUnassign: {
      return (
        <div className={css.jsondiff} data-action='unassign'>
          {!inline ?
           <Fragment>
             <div className={css.text}>Unassigned</div>
             {beforeFieldUrl ? <a href={beforeFieldUrl} target={'_blank'}>{beforeSpan}</a> : beforeSpan}
             <span className={css.text}>from</span>
             <span className={css.attributeName}>{label}</span>
           </Fragment> :
           <Fragment>
             {beforeFieldUrl ? <a href={beforeFieldUrl} target={'_blank'}>{beforeSpan}</a> : beforeSpan}
             <span className={css.text}>from</span>
             <span className={css.attributeName}>{label}</span>
           </Fragment>}
        </div>
      )
    }
    case isChange: {
      if (_.isArray(beforeValue) && _.isArray(afterValue)) {
        // Added / Removed

        label = null
      }

      return (
        <div className={css.jsondiff} data-action='change'>
          {/*<span className={css.text}>Changed</span>*/}
          {label && <span className={css.attributeName}>{label}</span>}
          <span className={css.text}>from</span>
          {beforeFieldUrl ? <a href={beforeFieldUrl} target={'_blank'}>{beforeSpan}</a> : beforeSpan}
          <span className={css.text}>to</span>
          {afterFieldUrl ? <a href={afterFieldUrl} target={'_blank'}>{afterSpan}</a> : afterSpan}
        </div>
      )
    }
  }
  return <div>???</div>
  // return <NestedFieldRenderer/>
})

const ScalarChangeValue = observer<{ className?: string, value, field: ICatalogFieldConfig }>(function ScalarChangeValue({className, value, field}) {
  //console.log(value, field)

  const type = _.isArray(value)
               ? 'array'
               : (field.def?.type === 'Float' || field.def?.type === 'Int' || field.def?.type === 'Double') ? 'scalar' : (field.def?.name == 'Date' || field?.def?.type === 'DateTime' || !isNaN(Date.parse(value)))
                 ? 'date'
                 : 'scalar',
    {strings} = useStores()

  // console.log(toJS({value, field}))
  return (
    <span className={classNames(className, css.scalarValue)}
          data-type={type}>
      {value !== null
       ? _.isArray(value)
         ? value.map((v, i) => <Fragment key={i}>{i > 0 ? ', ' : ''}<span className={css.arrayValue}>{v?.name ?? (React.isValidElement(v) ? v : v.toString())}</span></Fragment>)
         : type === 'date'
           ? <ReactTimeago date={new Date(value)}/>
           : _.toString(value) : strings.noValue}</span>
  )
})

const ScalarChangeRenderer = observer<{ className?: string }>(function ScalarChangeRenderer({className}) {
  const renderStore = useEventChangeRendererStore(),
    {label, beforeValue, afterValue, props: {inline}, field} = renderStore

  //console.log({operation, beforeValue, afterValue})

  return <div className={classNames(css.jsondiff, className)}>
    {!inline && <span className={css.text}>{beforeValue ? 'Changed' : 'Set'} </span>}
    <span className={css.attributeName}>{label}</span>
    {!beforeValue ? null :
     <Fragment>
       <span className={css.text}>from</span>
       <ScalarChangeValue className={css.before} value={beforeValue} field={field}/>
     </Fragment>}

    <span className={css.text}>to</span>
    <ScalarChangeValue className={css.after} value={afterValue} field={field}/>
  </div>
})

// endregion

class CatalogItemChangeEventStore extends VipLocalStore {
  constructor(stores: StoresInstance, public item: ICatalogItemChangeEventRaw) {
    super(stores)
  }

  get isImageEvent() {
    const {item: {operation}} = this
    return operation == 'ICREATE' || operation == 'IDELETE' || operation == 'IUPDATE'
  }

  // @computed get directObject() {
  //   const {keys, fieldByName} = this,
  //     first = _.first(keys)
  //   return keys.length > 1 ? 'multiple values' : first === 'attributes' ? _.startCase(first) : fieldByName(first)?.label
  // }

  findLeafNodes = (node, acc = [], path: string = '', esIndex?: string): Array<{ path: string, value, esIndex?: string }> => {
    const {data: {before, after}, findLeafNodes, fieldByName} = this
    //console.log({node, acc, path})
    if (acc.length > 25) return
    if (!_.isArray(node) && _.isObject(node)) {
      let associatedIndex = esIndex ?? (path && fieldByName(path)?.esIndex)
      if (path && fieldByName(path)?.esIndex && _.get(before, path)?.id != _.get(after, path)?.id) {
        // Child object changed
        //console.log(`child object assignment`, path, node)
        return [...acc, {path, value: node, esIndex: associatedIndex}]
      }

      //console.log(`recurse`, path, node)
      return _.keys(node).reduce(
        (acc, k) => {
          if (k === '_t') return acc

          // Index addition/deletion
          if (!_.isNaN(parseInt(k))) {
            return [...acc, {path, value: node[k], associatedIndex, action: node['_t']}]
          }
          return ([...acc, ...findLeafNodes(node[k], [], `${path}${path != '' ? '.' : ''}${k}`, associatedIndex)])
        },
        acc)
    } else if (path) {
      //console.log(`scalar assign`, path, node)
      return [...acc, {path, value: node, esIndex}]
    }
  }

  get operation() { return this.item.operation}

  get leaves() {
    const {delta} = this
    const o = _.isArray(delta) ? _.last(delta) : delta
    return this.findLeafNodes(o)
  }

  get multipleChanges() { return this.leaves?.length > 1}

  get verb(): string {
    const {item: {operation}, data: {before, after}, leaves, fieldByName} = this

    switch (operation) {
      case 'IMPORTED': {
        return 'imported'
      }
      case 'ICREATE': {
        return 'added an image:'
      }

      case 'IUPDATE': {
        return 'replaced an image:'
      }

      case 'IDELETE': {
        return 'removed an image:'
      }

      case 'CREATE': {
        return 'created'
      }

      case 'UPDATE': {
        if (leaves?.length == 1 && leaves[0].value?.length == 2 && leaves[0].value[0] == null) {
          return 'set'
        }

        if (leaves?.length == 1) {
          const {path} = _.first(leaves),
            beforeValue = _.get(before, path),
            afterValue = _.get(after, path),
            {esIndex} = (fieldByName(path) ?? {})

          if (esIndex) {
            if (!beforeValue) {
              return 'assigned'
            } else if (!afterValue) {
              return 'unassigned'
            }
          }
        }

        return 'changed'
      }

      case 'DELETE': {
        return 'deleted'
      }
    }

    return `??? (${operation})`
  }

  get showUpdate() {
    return true
  }

  get data() {
    return this.item.payload.data
  }

  get delta() {
    const {data: {before, after}} = this
    //console.log({before, after})
    try {
      if (_.isEqual(before, after)) return undefined
      return jsondiff.diff(before, after)
    } catch (err) {
      console.error(err)
      return undefined
    }
  }

  get keys() {
    return this.leaves?.map(v => v.path)
  }

  fieldByName = name => this.catalog._fieldByName[name]

  get inline() {
    const {multipleChanges, item: {operation}} = this

    return !(multipleChanges || operation == 'ICREATE' || operation == 'IUPDATE' || operation == 'IDELETE')
  }

  @computed get user(): Partial<IUser> {
    const {strings, stores: {users}, item: {userId, operation}} = this
    if (!userId || userId == 'null') {
      if (operation == 'IMPORTED') {
        return strings.importUser
      }
    }
    return users.getById(this.item.userId)
  }

  @computed get loading() {
    return _.isEmpty(this.user)
  }

  @computed get userIconText() {
    const {user: {shortName, name, firstName, lastName}} = this
    return shortName ? shortName : name ? _.first(name) : ''
  }

  @computed get beforeTitle() {
    const {catalog} = this, beforeValue = _.get(this.data.before, catalog.dataMap.title)

    return beforeValue ? beforeValue : catalog.catalogItemName
  }

  @computed get afterTitle() {
    const {catalog} = this, afterValue = _.get(this.data.after, catalog.dataMap.title)

    return afterValue ? afterValue : catalog.catalogItemName
  }
}

export const CatalogItemChangeEvent = observer<{ className?: string, item: ICatalogItemChangeEventRaw }>(({className, item}) => {
  const {_id, payload: {data: {after, before}}, operation, timestamp} = item,
    store = useVipLocalStore((ws) => new CatalogItemChangeEventStore(ws, item)),
    {
      verb, delta, multipleChanges, leaves, inline, userIconText, item: {userId},
      user: {image, name}, loading, beforeTitle, afterTitle, isImageEvent
    } = store

  if (!delta) {
    console.log(`Change event ${_id} has no change between before/after object - BUG IN EVENT LOGGER!`)
    return null
  }

  //console.log(jsondiff.formatters.console(delta, data.oldObj))

//if (_.isEmpty(user)) return null

  jsondiff.formatters.html.hideUnchanged()

  //console.log(toJS({verb, leaves, keys, user, data, delta}, {recurseEverything: true}))
  return (
    <CatalogItemChangeEventStoreContext.Provider value={store}>
      <div className={classNames(css.eventContainer, className)}>
        <div className={classNames(css.event)}>
          {loading ? <SkeletonGlowEvent/> :
           <Fragment>
             <CatalogItemChangeEventCard/>
           </Fragment>}
        </div>
        {!inline && !loading &&
		 <ul className={css.changes}>
           {leaves
             // Filter out creation events where no spec is set
             .filter(c => !(operation == 'CREATE' && _.isEmpty(c.value)))
             .map((change, i) => <li className={css.eventChange} key={i}>
               {loading ? <SkeletonGlowEvent/> : <IndividualEventChange change={change} inline={inline} key={i}/>}
             </li>)}
		 </ul>}
      </div>
    </CatalogItemChangeEventStoreContext.Provider>
  )
})

const CatalogItemChangeEventCard = observer(function CatalogItemChangeEventCard() {
  const store = useCatalogItemChangeEventStore(),
    {
      verb, multipleChanges, leaves, userIconText, item: {userId, operation, timestamp},
      user: {image, name}, beforeTitle, afterTitle, catalog
    } = store

  return (<Fragment>
    <div className={css.userEventCard}>
      <div className={css.userIcon}>
        {_.isEmpty(image) ? <Placeholder type={'small'} noImageText={userIconText}/> : <MyImage imageUrl={catalog.imageUrlFor({exId: image})}/>}
      </div>

      <div className={css.column}>
        <div className={css.row}>
          <span className={css.userName} data-user-id={userId}>{name ?? 'Unknown User'}</span>
          <span className={css.verb}>{operation == 'UPDATE' && multipleChanges ? `made ${leaves.length} changes` : verb}{operation == 'UPDATE' && multipleChanges ? ':' : ''}</span>

          {operation == 'CREATE' || operation == 'IMPORTED'
           ? <a className={css.afterTitle}>{afterTitle}</a> // todo Convert to hyperlink
           : operation == 'DELETE'
             ? <a className={css.beforeTitle}>{beforeTitle}</a>// todo Convert to hyperlink
             : operation == 'UPDATE' && !multipleChanges
               ? <IndividualEventChange change={_.first(leaves)} inline/>
               : null}
          {/*{stringify(item)}*/}
        </div>

        <span className={css.timestamp}><ReactTimeago date={new Date(timestamp)}/></span>
      </div>
    </div>
  </Fragment>)
})


// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/CatalogItemChangeHistory/CatalogItemChangeEvent.tsx